import {EventFilters} from './api/use-event-filters'
import {EventLog} from '@cypro/engine/schema'
import {applyPatch, type Operation} from 'rfc6902'

export const toDayKey = (value: number) => {
  const d = new Date(value)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

export type FilterOption = {
  label: string
  value: string
}

export type TransformedFilters =
  | {
      [K in keyof EventFilters]: FilterOption[]
    }
  | undefined

export function transformFiltersToOptions(
  data: EventFilters | undefined,
  t: (key: string) => string
): TransformedFilters {
  // this function converts the filter values fetched from the backend
  // to a label/value pair format, using translation where needed for labels

  if (!data) return

  const transformWithTranslation = (values: string[], prefix: string) =>
    values
      .map(value => ({
        label: t(`${prefix}${value}`),
        value,
      }))
      .toSorted((a, b) => a.label.localeCompare(b.label))

  return {
    status: transformWithTranslation(data.status, 'log_status_'),
    action: transformWithTranslation(data.action, 'event.action.'),
    performedBy: data.performedBy.map(actor => ({
      label: actor.name || actor.id,
      value: actor.id,
    })),
    domain: transformWithTranslation(data.domain, 'event.domain.'),
    resource: transformWithTranslation(data.resource, 'resource.'),
  }
}

export function mapOldStateToEvents(events: EventLog[]) {
  const mappedEvents: (EventLog & {state: Record<string, unknown>})[] = []
  if (!events?.length) return mappedEvents

  for (let i = 0; i < events.length; i++) {
    const log = events[i]
    const prevEvent = mappedEvents[i - 1]

    let state: Record<string, unknown> = {}
    if (prevEvent?.state) state = {...prevEvent.state}

    const safePatches: Operation[] = (log.patches || []).map(p => (p.op === 'replace' ? {...p, op: 'add' as const} : p))
    applyPatch(state, safePatches)

    mappedEvents.push({...log, state})
  }

  return mappedEvents
}

export interface ValueChange {
  op: 'add' | 'replace'
  path: string
  value: unknown
  oldValue: unknown
}

export function mapOldValuesToLogPatches(log: EventLog, mappedEvents: ReturnType<typeof mapOldStateToEvents>) {
  const value: ValueChange[] = []
  if (!log.patches) return value

  const logIndex = mappedEvents?.findIndex(l => l.id === log.id)
  const prevLog = mappedEvents?.[logIndex - 1]
  const prevState = prevLog?.state
  // A `test` op written before a change records the old value, so it shows even without earlier logs (e.g. migrated data).
  const testedValues = new Map(log.patches.filter(p => p?.op === 'test').map(p => [p.path, p.value]))

  for (const patch of log.patches) {
    if (!patch) continue

    if (patch.op === 'add' || patch.op === 'replace') {
      const path = patch.path?.replace('/', '')
      const oldValue = testedValues.has(patch.path) ? testedValues.get(patch.path) : prevState?.[path]

      if (oldValue === patch.value) continue

      value.push({...patch, path, oldValue})
    }
  }

  return value
}

export function getLogEvent(log: EventLog) {
  if (log.event) return log.event
  if (log.action && log.resource) return `${log.action}.${log.resource}`
  return 'unknown'
}

export function getLogDomain(log: EventLog) {
  if (log.domain) return log.domain
  if (log.resource?.includes('patient')) return 'patient'
  return 'configuration' // includes most of the events
}

export function getLogStatus(log: EventLog) {
  if (log.status) return log.status
  return 'OK' // if no explicit status is set, probably the event is OK
}
